import AccountCircle from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { alpha, styled } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import {
  useDispatch,
  useSelector,
} from '../../../../node_modules/react-redux/dist/react-redux';
import { logout } from '../../../app/user/userStateSlice';
import { clearLocalStoragePreservingPreferences } from '../../../app/user/logoutUtils';
import ColorModeIconDropdown from '../../../shared-theme/ColorModeIconDropdown';
import { getEnabledMenuItems, hasAnyAdminPermission } from '../../../pages/utilities';
import OwlLogo from '../../../images/owl-white.svg';
import '.././navigationCss/cssOverrideMenu.css'; // Scoped CSS only for this component

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

export default function PrimarySearchAppBar() {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [gearAnchorEl, setGearAnchorEl] = React.useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = React.useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [cookies, removeCookie] = useCookies(['login_token']);

  const isMenuOpen = Boolean(anchorEl);
  const isGearMenuOpen = Boolean(gearAnchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const { userName } = useSelector((state) => state.user.profile);
  const { actions, availableForms, loggedIn, actionsLoaded } = useSelector(
    (state) => state.user,
  );
  const enabledItems = getEnabledMenuItems(actions, availableForms);
  const showAdminItem = hasAnyAdminPermission(enabledItems);
  // Same condition Dashboard.jsx uses to swap in <NoRolesWelcome />. While in
  // that state, Preferences and Notifications are dead-ends (no role-scoped
  // data to configure or receive), so disable the menu items.
  const hasNoRoles = loggedIn && actionsLoaded && actions.length === 0;
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleGearMenuOpen = (event) => {
    setGearAnchorEl(event.currentTarget);
  };

  const handleGearMenuClose = () => {
    setGearAnchorEl(null);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleSignOut = async () => {
    try {
      // Call logout endpoint to clear server-side session
      await fetch('/api/logout', {
        method: 'GET',
        credentials: 'include',
      });

      // Remove the login_token cookie
      removeCookie('login_token', null);

      // Dispatch logout action to clear Redux state
      dispatch(logout());

      // Clear any local storage/session (preserve user preferences like color mode)
      clearLocalStoragePreservingPreferences();
      sessionStorage.clear();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        disableRipple
        disabled
        sx={{
          fontWeight: 500,
          color: 'text.secondary',
          cursor: 'default',
          userSelect: 'text',
          // In the no-role state, let MUI's default disabled opacity dim the
          // name to match the disabled Preferences/Notifications items below
          // — it should read as "this whole menu is on hold" not "your name
          // is the one active thing here."
          '&.Mui-disabled': hasNoRoles
            ? {}
            : { opacity: 1, color: 'text.secondary' },
          '&:hover': { backgroundColor: 'transparent' },
        }}
      >
        {userName?.replace(/\s*\(local\)/i, '')}
      </MenuItem>
      <MenuItem
        disabled={hasNoRoles}
        onClick={() => {
          window.location.hash = '#/preferences';
          handleMenuClose();
        }}
      >
        Preferences
      </MenuItem>
      <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
    </Menu>
  );

  const gearMenuId = 'primary-search-gear-menu';
  const renderGearMenu = (
    <Menu
      anchorEl={gearAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={gearMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isGearMenuOpen}
      onClose={handleGearMenuClose}
    >
      {showAdminItem && (
        <MenuItem
          onClick={() => {
            window.location.hash = '#/admin';
            handleGearMenuClose();
          }}
          data-testid="header-menu-admin"
        >
          Admin Settings
        </MenuItem>
      )}
      <MenuItem
        disabled={hasNoRoles}
        onClick={() => {
          window.location.hash = '#/notifications';
          handleGearMenuClose();
        }}
      >
        Notifications
      </MenuItem>
    </Menu>
  );

  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem onClick={() => { window.location.hash = '#/notifications'; handleMobileMenuClose(); }}>
        <IconButton
          size="small"
          aria-label="notifications"
          color="inherit"
        >
          <NotificationsIcon />
        </IconButton>
        <p>Notifications</p>
      </MenuItem>
      {showAdminItem && (
        <MenuItem
          onClick={() => {
            window.location.hash = '#/admin';
            handleMobileMenuClose();
          }}
          data-testid="header-mobile-menu-admin"
        >
          <IconButton
            size="small"
            aria-label="admin"
            color="inherit"
          >
            <AdminPanelSettingsOutlinedIcon />
          </IconButton>
          <p>Admin Settings</p>
        </MenuItem>
      )}
      <MenuItem
        onClick={() => {
          handleMobileMenuClose();
          handleSignOut();
        }}
      >
        <p>Sign out</p>
      </MenuItem>
    </Menu>
  );

  return (
    <Box
      sx={{
        width: '100%',
        flexShrink: 0,
      }}
    >
      <AppBar position="static">
        <Toolbar
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            backgroundColor: '#154F85!important',
          }}
        >
          <Box
            onClick={() => navigate('/')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <Box
              component="img"
              src={OwlLogo}
              alt=""
              aria-hidden="true"
              sx={{ height: 24, width: 'auto', display: 'block' }}
            />
            <Typography
              fontFamily={"'Arial'"}
              variant="h6"
              noWrap
              component="div"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              ATHENA
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />
          {/* <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder=""
                inputProps={{ 'aria-label': 'search' }}
              />
            </Search> */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ColorModeIconDropdown />
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 2,
              }}
            >
              <IconButton
                size="medium"
                aria-label="open settings menu"
                aria-controls={gearMenuId}
                aria-haspopup="true"
                onClick={handleGearMenuOpen}
                sx={[
                  { border: 0 },
                  { borderRadius: '0px!important' },
                  { backgroundColor: '#154F85!important' },
                  { color: (theme) => theme.palette.secondary.contrastText },
                  { ':hover': { backgroundColor: '#446d93ff!important' } },
                  { padding: '0px!important' },
                ]}
              >
                <SettingsOutlinedIcon />
              </IconButton>
              <IconButton
                size="medium"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                sx={[
                  { border: 0 },
                  { borderRadius: '0px!important' },
                  { backgroundColor: '#154F85!important' },
                  { color: (theme) => theme.palette.secondary.contrastText },
                  { ':hover': { backgroundColor: '#446d93ff!important' } },
                  { padding: '0px!important' },
                ]}
              >
                <AccountCircle />
              </IconButton>
            </Box>
            <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="medium"
                aria-label="show more"
                aria-controls={mobileMenuId}
                aria-haspopup="true"
                onClick={handleMobileMenuOpen}
              >
                <MoreIcon />
              </IconButton>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
      {renderGearMenu}
    </Box>
  );
}
